import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  orderBy,
  where,
  deleteDoc,
  updateDoc,
  FirestoreError,
} from 'firebase/firestore';
import { firestore } from '../../config/firebase';
import { AdminUser, CourseComment, CourseRating } from '@e-learning/shared/src/store/types';

/**
 * Check if user is admin (checks both users collection and auth custom claims)
 */
export const checkAdminStatus = async (userId: string): Promise<boolean> => {
  try {
    const userRef = doc(firestore, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const data = userSnap.data();
      // Check both isAdmin custom property and role field for backward compatibility
      return data.isAdmin === true || data.role === 'admin';
    }
    return false;
  } catch (error) {
    return false;
  }
};

/**
 * Get user admin status from Firestore
 */
export const getUserAdminStatus = async (userId: string): Promise<boolean> => {
  return checkAdminStatus(userId);
};

/**
 * Fetch all users
 */
export const fetchAllUsers = async (): Promise<AdminUser[]> => {
  try {
    const usersRef = collection(firestore, 'users');
    const q = query(usersRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const users: AdminUser[] = [];
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      users.push({
        uid: docSnapshot.id,
        email: data.email || '',
        displayName: data.displayName || null,
        role: data.role || 'user',
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || '',
      } as AdminUser);
    });
    
    return users;
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to fetch users: ${firestoreError.message}`);
  }
};

/**
 * Update user role
 */
export const updateUserRole = async (userId: string, role: 'admin' | 'user'): Promise<void> => {
  try {
    const userRef = doc(firestore, 'users', userId);
    await updateDoc(userRef, {
      role,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to update user role: ${firestoreError.message}`);
  }
};

/**
 * Delete a comment (admin only)
 */
export const deleteComment = async (commentId: string, courseId: string): Promise<void> => {
  try {
    const commentRef = doc(firestore, 'comments', commentId);
    await deleteDoc(commentRef);
    
    // Update course comments count
    const courseRef = doc(firestore, 'courses', courseId);
    await updateDoc(courseRef, {
      totalComments: (await getDoc(courseRef)).data()?.totalComments || 0 - 1,
    });
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to delete comment: ${firestoreError.message}`);
  }
};

/**
 * Delete a rating (admin only)
 */
export const deleteRating = async (ratingId: string, courseId: string): Promise<void> => {
  try {
    const ratingRef = doc(firestore, 'ratings', ratingId);
    await deleteDoc(ratingRef);
    
    // Note: You may want to recalculate average rating for the course
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to delete rating: ${firestoreError.message}`);
  }
};

/**
 * Delete a blog comment (admin only)
 */
export const deleteBlogComment = async (commentId: string, blogId: string): Promise<void> => {
  try {
    const commentRef = doc(firestore, 'blogComments', commentId);
    await deleteDoc(commentRef);
    
    // Update blog comments count
    const blogRef = doc(firestore, 'blogs', blogId);
    const blogData = (await getDoc(blogRef)).data();
    await updateDoc(blogRef, {
      commentsCount: Math.max(0, (blogData?.commentsCount || 0) - 1),
    });
  } catch (error) {
    const firestoreError = error as FirestoreError;
    throw new Error(`Failed to delete blog comment: ${firestoreError.message}`);
  }
};

